import type {
  AnalyticsPluginConfig,
  EditProxyFormat,
  FocusPoint,
  Ingest,
  KeldaIngest,
  PagedProxy,
  GeneratedContentLocation,
  CloudIngestLocation,
  CloudArchiveLocation,
  HighresDeletion,
  MediaLocationTypes,
  ArchiveInfo,
  CentrallyRenderedSequenceInfo,
} from './itemPropTypes';
import type { PendingSubclipOperation } from '@mimir/common/dto/subclipDto';
import type { Celebrity, Label, Person } from './searchModels';
import type { LanguageCode } from './asyncOperationItemCreationTypes';
import type { AsyncOperationState } from './asyncOperationPrimitives';
import type { ItemMetaDataDto, TechnicalMetadata } from './fieldValue';
import type { SequenceDetails } from '@mimir/primitive/nle/sequence';
import type { ItemAccessControl } from './itemAccessControlDto';
import type { ExternalProxyDto } from './externalProxyDto';
import type { MediaAvailability } from './mediaAvailability';
import type { GeoShape } from './GeoLocation';
import type { ItemAttachmentDto } from './itemAttachmentDto';
import type { MediaIssue, MediaIssueType } from './mediaIssueDto';
import type { RelationsEntity } from './relationsEntity';
import type { UploadMethods } from '../upload/upload-types';
import type { UploadProgressInfo } from '../upload/uploadProgression';
import type { ProgressInfo } from './progress';
import type { FileLocation, ProxyFileLocation } from './fileLocation';
import type { TextDetectionSettings } from './textDetectionSettings';
import type { NonScheduledRecording, LiveRecording } from './liveItemProps';
import type { ScheduledRecording } from './scheduledRecording';
import type { SearchItemType } from './itemTypes';
import type { ItemStateType } from './itemState';
import type { VirtualClipModel } from './virtualClipModel';
import type { MediaUsage } from './itemMediaUsageDto';
import type { AudioDownMix } from './audioMixingDtos';
import type { LivePreviewLocation } from './livePreviewLocation';
import type { TaskInfo } from './taskInfo';
import type { TamsReference } from './tams';
import type { ThumbnailInfo } from './thumbnailInfo';
type CloudHighresLocation = CloudIngestLocation & CloudArchiveLocation;

export interface ExtendedItemDto extends CloudHighresLocation {
  // Directly reflected entity attribute
  id: string;

  // Directly reflected entity attribute
  metadataVersion: number;

  /**
   * Directly reflected entity attribute
   *
   * @deprecated Use metadata.formData[SystemDefaultField.Title] instead.
   */
  title?: string;

  // Directly reflected entity attributes
  personDetectionErrorMessage?: string;
  transcriptionErrorMessage?: string;
  celebrityDetectionErrorMessage?: string;
  labelDetectionErrorMessage?: string;
  textDetectionErrorMessage?: string;
  transcodingErrorMessage?: string;
  personDetectionState?: AsyncOperationState | null;
  transcriptionState?: AsyncOperationState | null;
  celebrityDetectionState?: AsyncOperationState | null;
  labelDetectionState?: AsyncOperationState | null;
  textDetectionState?: AsyncOperationState;
  transcodingState?: AsyncOperationState | null;
  archiveFileTransferState?: AsyncOperationState;
  archiveFileTransferErrorMessage?: string;

  /**
   * Directly reflected entity attribute
   *
   * @deprecated Use metadata.formData[SystemDefaultField.CreatedOn] instead.
   */
  createdOn?: string;

  // Directly reflected entity attributes
  itemState: ItemStateType;
  isDeleting?: boolean;
  folderParents: string[];

  /*
    Directly reflected entity attribute.

    Not all item resources has this field. For example, placeholders do not have
    any media type.

    This value is used when creating presigned URLs to highres resoures to
    ensure that the resource is served out with the appropriate content type.
  */
  mediaType?: string;


  /**
   * @deprecated Use technicalMetadata.formData[TechnicalVideoDataField.FrameRate] instead
   */
  mediaFrameRate?: number;

  // Directly reflected entity attribute. Set to null in update to clear.
  externalId?: string | null;

  // Directly reflected entity attribute.
  metadata: ItemMetaDataDto;
  /**
   * Used for glacier archive to S3.
  */
  archiveInfo?: ArchiveInfo;
  /**
   * when the archive file transfer to s3 complete.
  */
  archivedOn?: string;
  uploadProgressInfo?: UploadProgressInfo | null;
  progressInfo?: ProgressInfo;
  warnings?: string[];
  mediaIssues?: MediaIssue[];

  /*
    Directly reflected entity attribute.

    Not all item resources has this field. For instance placeholders do not have
    it. There are remnants in createItem of code deriving originalFileName from
    title, but that code is not used by the Mimir UI, since the Mimir UI now
    supplies metadata.formData.default_title instead of title.

    This value is used when creating presigned URLs to highres resoures to
    ensure that the file name saved during download is the same as the original
    even though the S3 object key may be different.
  */
  originalFileName?: string;

  /*
    Directly reflected entity attribute.

    Not all item resource has this field. For instance an item created by HTTP
    POST of body '{}' to createItem API do not have it.

    TODO: Consider whether createItem API show disallow creating item resources
    without itemType. The Mimir UI already forces the user to pick between video
    and image.

    TODO: itemType here shows that ExtendedItemDto as a type doesn't make much
    sense. Search result DTOs allow itemType to be 'folder' while getItem() DTOs
    do not. Using ExtendedItemDto as a common base therefore, becomes a mushy
    type type definition.
  */
  itemType?: SearchItemType;

  // Directly reflected entity attribute.
  mediaSize?: number;
  proxySize?: number;

  /*
    Directly reflected entity attributes.

    Retrieved by StorageManager through getItem API with custom fields list.

    Means that edit-proxy generation was enabled when transcoding this item.
    Does not mean it is already available.
  */
  hasEditProxy?: boolean;
  hasProxy?: boolean;

  // Should not be reflected
  editProxyUrl?: string;

  /**
   * Directly reflected entity attribute
   *
   * True when edit-proxy is produced by kelda and mxcoder just copies it from
   * incoming to generated media bucket. Does not mean it is already availble.
   */
  editProxyIngested?: boolean;

  /**
   * Directly reflected entity attribute.
   *
   * True when item has external highres info stored in separate DynamoDB table.
   */
  hasMediaIngestIntegrationExternalHighres?: boolean;

  isInOfflineStorage?: boolean;

  /**
   * Directly reflected entity attribute
   *
   * True when editProxyAvailable is available to download from generated media bucket.
   */
  editProxyAvailable?: boolean;


  /**
   * Observed values in stage:dev
   * ```
   *  197896 null
   *    4414 "mpegts"
   *    2370 "h264"
   *      15 "prores"
   * ```
   */
  editProxyFormat?: EditProxyFormat;

  pagedProxy?: PagedProxy;

  /**
   * Signals the availability of incrementally ingested (live) media.
   *
   * Directly reflected entity attribute.
   */
  media?: MediaAvailability;

  ingest?: Ingest;

  keldaIngest?: KeldaIngest;

  // Directly reflected entity attributes
  transcriptionEnabled?: boolean;
  languageCode?: LanguageCode;
  transcriptionService?: string;
  labelDetectionEnabled?: boolean;
  textDetectionEnabled?: boolean;
  personDetectionEnabled?: boolean;
  createUnknownPersons?: boolean;
  celebrityDetectionEnabled?: boolean;
  transcriptionChannels?: number[] | null;
  transcriptionChannelIdentification?: boolean | null;
  isExternalUpload?: boolean;
  detectedPersons?: Person[];
  detectedCelebrities?: Celebrity[];
  detectedLabels?: Label[];

  transcodingJobCanceled?: boolean;
  transcriptionJobCanceled?: boolean;
  personDetectionJobCanceled?: boolean;
  celebrityDetectionJobCanceled?: boolean;
  labelDetectionJobCanceled?: boolean;
  textDetectionJobCanceled?: boolean;

  // Stored in S3 rather than DynamoDB item
  sequenceDetails?: SequenceDetails;
  geoLocation?: GeoShape;

  /*
    Derived property calculated by getItem API based on entity attributes
    virtualClipInfo, id, itemType and hasProxy.

    Retrieved by StorageManager through getItem API with custom fields list.
  */
  proxy: string | null;

  /*
    Derived property calculated by getItem API based on entity attributes
    archiveInfo, virtualClipInfo, id, archiveS3Bucket, archiveLocationFullPath,
    originalFileName, mediaType, tenantId and ingestStorageId.

    Retrieved by StorageManager through getItem API with custom fields list.
  */
  highRes?: string;

  // Directly reflected entity attribute.
  technicalMetadata?: TechnicalMetadata

  // When an item is uploaded through the Mimir web client, the method of upload will be tracked here.
  // For multi-file items, this will contain the upload method for each individual item.
  uploadMethods?: UploadMethods;

  //Property that indicate archive status
  isArchived?: boolean;

  visibleTo?: ItemAccessControl;
  createdBy?: string;
  /**
   * Defines who can update certain properties of this item. Currently only covers metadata and timed metadata.
   */
  updatableBy?: ItemAccessControl;

  // location information for generated aaf file for items ingested from Avid Mediafiles folder
  aafLocation?: FileLocation;
  /**
   * If not undefined, points to an Avid aaf file stored in the
   * generated media bucket folder of this item.
   */
  aaf?: string;

  // Indicates the item has proxy in an external storage instead of the generated bucket
  externalProxyInfo?: ExternalProxyDto;

  // Users are able to set x,y coordinates on images to indicate a focus point.
  // this is only served if explicitly set by a user.
  focusPoint?: FocusPoint | null;

  // Update-only properties, which can be updated, but not retrieved
  sourceFileLocation?: MediaLocationTypes;
  reprocessBy?: string;

  // Read-only, implicitly updated properties:

  /**
   * Modification timestamp in Date.toISOString() format.
   *
   * Read-only. Implicitly updated.
   */
  modifiedOn?: string;
  purgeDate?: string;

  attachments?: ItemAttachmentDto[]
  // May be set by Kelda when processing audio only Avid material
  frameCount?: number;

  liveRecording?: LiveRecording;
  nonScheduledRecording?: NonScheduledRecording;
  scheduledRecording?: ScheduledRecording;

  /**
   * If defined, indicates where to find generated media content.
   *
   * If not defined, all the content for a given item is found in
   * <generated media bucket>/<item id>/
   */
  generatedContentLocation?: GeneratedContentLocation;

  /**
   * If the item can be considered as live or not.
   * Only indexed in OpenSearch
   */
  isLive?: boolean;

  timecode?: string;

  conformDetailsIfRenderedSequence?: CentrallyRenderedSequenceInfo;

  isP2Part?: boolean;
  isHidden?: boolean;

  isSubclip?: boolean;
  pendingSubclipOps?: PendingSubclipOperation[];

  relationsEntity?: RelationsEntity;
  /**
   * Signed URL to the sequenceDetails JSON file which is available for items that were
   * created by exporting a Premiere sequence from the panel. Computed in getItem API.
   */
  exportedSequenceDetailsUrl?: string;

  textDetectionSettings?: TextDetectionSettings;

  analyticPlugins?: Record<string, AnalyticsPluginConfig>;

  /**
   * This property is used to set when the media was last used.
   * We're currently considering the following use cases:
   *  - item being import on premiere
   *  - item being used during central or local rendering
   *
   * @deprecated used `mediaUsage` instead
   */
  mediaLastUsedOn?: string;

  /**
   * This property is used to set mediaUsage by the Mimir client
   * to determine when and how the media was last used.
   */
  mediaUsage?: MediaUsage;

  /**
   * A property to store info about ongoing tasks or operations performed on the item.
   * This allows us to retrieve details about various tasks, such as ongoing render sequences,
   * kelda requests, or pending operations etc.
   *
   * The purpose of this property is to bookkeeping the operation-specific details directly
   * at the item level, avoiding the need for external lookups, filters, or scans to retrieve
   * the task-specific details.
   *
   * NOTE: We store the `taskEntries` as a serialized string set in the item entity. This allows
   * us to perform atomic updates on the taskInfo field, enabling users to add or remove taskInfo
   * concurrently without losing data. The item entity mapper is responsible for serializing and
   * deserializing the `taskEntries` as needed.
   */
  taskEntries?: TaskInfo[];

  /**
   * Information regarding highRes deletion operation. Exists if user makes a request to delete the highRes.
   */
  highresDeletion?: HighresDeletion;

  // Derived from archiveS3Bucket and archiveLocationFullPath, which are not exposed in every response.
  highresInArchiveBucket?: boolean;

  editProxyLocation?: ProxyFileLocation;
  webProxyLocation?: ProxyFileLocation;

  indexLocation?: FileLocation;
  indexUrl?: string;

  virtualClipInfo?: VirtualClipModel;
  mediaDuration: number;

  /**
   * Information about audio channel down-mixing.
   */
  webProxyAudioDownMixInfo?: AudioDownMix;

  /**
   * Settings for post-processing operations that should be performed after initial ingestion.
   */
  postProcessSettings?: PostProcessSettings;

  livePreviewLocation?: LivePreviewLocation;

  rewrapCompatibilityHash?: string;

  hasRelations?: boolean;

  /** Derived from mediaIssues, not stored. */
  mediaIssueTypes?: MediaIssueType[];

  /**
   * The offset in milliseconds by which all the words in the current transcript are shifted.
   */
  transcriptOffsetMs?: number;
  customIcons?: ItemCustomIconsDto;

  /**
   * Item is linked to media in TAMS (Time Addressable Media Storage)
   */
  tamsReference?: TamsReference,

  thumbnailInfo?: ThumbnailInfo;
}

/**
 * Settings for post-processing operations to be performed after initial ingestion.
 */
export type PostProcessSettings = PostProcessSettingsV1;

export interface PostProcessSettingsV1 {
  type: 'v1';
  /**
   * Flag indicating that this item requires flat web proxy generation during post-processing.
   * Used for TS live folder ingestion to trigger web proxy generation after recording completes.
   */
  shouldGenerateFlatWebProxy: boolean;
}

export const itemCustomIconSlots = ['pos1', 'pos2', 'pos3', 'pos4', 'pos5', 'pos6', 'pos7', 'pos8'] as const;

export type ItemCustomIconSlot = typeof itemCustomIconSlots[number];

export type ItemCustomIconsDto = Partial<Record<ItemCustomIconSlot, ItemCustomIconDto | null>>;

export type ItemCustomIconUpdateDto = {
  customIcons: ItemCustomIconsDto
};

export interface ItemCustomIconDto {
  icon: string;
  tooltip?: string;
}

export interface ItemCustomIconBlendedDto {
  iconIdentifier: string;
  iconUrl: string;
  tooltip?: string;
}

export type ItemCustomIconsBlendedDto = (ItemCustomIconBlendedDto | null)[];
